import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface NotesSectionProps {
  notes: string;
  onUpdate: (notes: string) => void;
}

const NotesSection = ({ notes, onUpdate }: NotesSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const defaultTemplate = `Thank you for your interest in Evoqia solutions.\n\nThis quote is valid for 30 days from the date of issue. All prices are in EUR and exclude VAT.\n\nLicense subscriptions are billed monthly in advance. Implementation services are billed upon completion based on actual hours worked.\n\nFor any questions regarding this quote, please contact sales@evoqia.eu.\n\nWe look forward to working with you.`;

  const handleLoadTemplate = () => {
    onUpdate(defaultTemplate);
    setIsEditing(true);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded transition-colors duration-150"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            <Icon
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
              size={20}
              className="text-text-secondary"
            />
          </button>
          <Icon name="FileText" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">Notes & Terms</h2>
        </div>
        {isExpanded && (
          <div className="flex items-center gap-2">
            {!notes && (
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                iconPosition="left"
                onClick={handleLoadTemplate}
              >
                Load Template
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName={isEditing ? 'Eye' : 'Edit'}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Preview' : 'Edit'}
            </Button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="p-6">
          {isEditing ? (
            <textarea
              value={notes}
              onChange={(e) => onUpdate(e.target.value)}
              placeholder="Enter quote notes, terms, and conditions..."
              className="w-full min-h-[200px] p-4 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y text-sm text-text-primary bg-background"
            />
          ) : (
            <div className="min-h-[200px] p-4 bg-muted rounded-lg">
              {notes ? (
                <div className="text-sm text-text-primary whitespace-pre-wrap">{notes}</div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-text-secondary">
                  <Icon name="FileText" size={48} className="mb-2 opacity-30" />
                  <p>No notes added yet</p>
                  <p className="text-xs mt-1">Click "Load Template" or "Edit" to add notes</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesSection;